// @ts-check
// =============================================================================
// js/ui/grid-size.js — the shared card-scale control.
//
// The gallery and the deck builder share ONE setting: the base card WIDTH. It
// is published as `--card-w` on the active screen, so everything scales off it
// together:
//   • cardpool cards + leaders : var(--card-w)         (100%)
//   • deck (visual) cards       : calc(var(--card-w) * 0.66)
// The control is [−] [Auto size] [+]: − / + step the width, Auto returns to the
// responsive CSS default (the `--card-w` fallback). Stepping away from Auto
// first MEASURES the current card width so the first press is a real nudge from
// what's on screen. Persisted across visits.
// =============================================================================

const MIN_W = 90;   // px — smallest card
const MAX_W = 320;  // px — biggest card
const STEP = 26;    // px per press

/** 0 = Auto (the responsive CSS default). */
let cardW = Number(localStorage.getItem('cardWidth')) || 0;
if (!Number.isFinite(cardW) || cardW < 0 || cardW > MAX_W) cardW = 0;

function persist() {
	try {
		localStorage.setItem('cardWidth', String(cardW));
	} catch {
		// storage full/blocked — the session still works
	}
}

/** The screen that should carry --card-w (so leaders + deck + pool all inherit). */
function scaleRoot(gridEl) {
	return /** @type {HTMLElement|null} */ (gridEl?.closest?.('.screen')) || /** @type {HTMLElement|null} */ (gridEl);
}

/**
 * Publish the current card width to the screen (or clear it for Auto).
 * @param {Element|null} gridEl  A card grid (.gallery-grid / #deck-pool).
 * @returns {void}
 */
export function applyGridSize(gridEl) {
	const root = scaleRoot(gridEl);
	if (!root) return;
	if (cardW) {
		root.style.setProperty('--card-w', cardW + 'px');
	} else {
		root.style.removeProperty('--card-w');
	}
}

/**
 * The width a pool card is CURRENTLY rendered at (for stepping away from Auto).
 * @param {HTMLElement|null} gridEl
 * @returns {number}
 */
function measureCardWidth(gridEl) {
	const shell = gridEl?.querySelector('.gallery-shell, .deck-pool-card');
	const w = shell?.getBoundingClientRect().width;
	return w ? Math.round(w) : 170;
}

/**
 * Mount the [−] [Auto size] [+] control into a slot element.
 * @param {HTMLElement|null} slotEl        Where the buttons live.
 * @param {() => Element|null} getGridEl   Resolves the grid (may repaint).
 * @returns {void}
 */
export function mountGridSizeControls(slotEl, getGridEl) {
	if (!slotEl) return;

	const set = (nextW) => {
		cardW = nextW;
		persist();
		applyGridSize(getGridEl());
		paint();
	};
	const step = (deltaPx) => {
		const from = cardW || measureCardWidth(/** @type {HTMLElement|null} */ (getGridEl()));
		set(Math.max(MIN_W, Math.min(MAX_W, from + deltaPx)));
	};

	const paint = () => {
		slotEl.innerHTML = '';
		slotEl.className = 'grid-size-controls';
		const button = (label, onClick, title, { active = false, disabled = false } = {}) => {
			const el = document.createElement('button');
			el.className = 'defaultButton grid-size-btn' + (active ? ' is-active' : '');
			el.textContent = label;
			el.title = title;
			el.disabled = disabled;
			el.addEventListener('click', onClick);
			slotEl.appendChild(el);
		};
		// "+" grows the cards, "−" shrinks them; disabled at the size limits.
		button('−', () => step(-STEP), 'Smaller cards', { disabled: !!cardW && cardW <= MIN_W });
		button('Auto size', () => set(0), 'Automatic sizing', { active: cardW === 0 });
		button('+', () => step(STEP), 'Bigger cards', { disabled: !!cardW && cardW >= MAX_W });
	};
	paint();
}
